/**
 * Unified Apollo Client for Web
 *
 * ARCHITECTURE PRINCIPLE:
 * - Single source of truth for Apollo Client across entire application
 * - Works in both React (hooks) and non-React (services/jobs) contexts
 * - Token management through a unified getter function
 * - Full token refresh with queue pattern matching website's Apollo client
 *
 * USAGE:
 * 1. App initializes: setUnifiedClient(client, tokenGetter) OR uses createCoreApolloClient()
 * 2. Modules use: getUnifiedClient() for non-React contexts
 * 3. React hooks continue to use ApolloProvider from app
 *
 * TOKEN REFRESH FLOW:
 * 1. Request fails with auth error (401/Unauthorized)
 * 2. Queue subsequent requests while refreshing
 * 3. Call doRefreshToken via raw fetch (bypasses Apollo to avoid circular error handling)
 * 4. On success: update token, process queued requests, retry failed operation
 * 5. On failure: clear auth data, reject queued requests
 */

import {
    ApolloClient,
    InMemoryCache,
    HttpLink,
    ApolloLink,
    Observable,
    type NormalizedCacheObject,
    type DefaultContext,
    type InMemoryCacheConfig,
    type FetchResult,
} from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';
import type { NetworkError } from '@apollo/client/errors';
import { setContext } from '@apollo/client/link/context';
import { onError, type ErrorResponse } from '@apollo/client/link/error';
import type { Operation, NextLink } from '@apollo/client/link/core';
import { appConfig } from '../config';
import { logSentry } from '../sentry';

// ============================================================================
// OBSERVABLE HELPER (replaces deprecated fromPromise)
// ============================================================================

/**
 * Create an Observable from a Promise
 * Replaces deprecated `fromPromise` from Apollo Client
 * @see https://www.apollographql.com/docs/react/api/link/introduction
 */
function observableFromPromise<T>(promise: Promise<T>): Observable<T> {
    return new Observable<T>(subscriber => {
        promise
            .then(value => {
                subscriber.next(value);
                subscriber.complete();
            })
            .catch(err => {
                subscriber.error(err);
            });
    });
}

/**
 * Extract error details from Apollo ErrorResponse
 * Centralizes access to deprecated properties (graphQLErrors, networkError)
 * for easier migration to Apollo Client 4.0 where these are consolidated into `error`
 */
function extractErrorDetails(errorResponse: ErrorResponse): {
    graphQLErrors: ReadonlyArray<GraphQLFormattedError> | undefined;
    networkError: NetworkError | undefined;
} {
    // Apollo 3.x: these properties are deprecated but have no replacement until 4.0
    // In 4.0, they will be consolidated into the `error` property
    const raw = errorResponse as unknown as Record<string, unknown>;
    return {
        graphQLErrors: raw.graphQLErrors as ReadonlyArray<GraphQLFormattedError> | undefined,
        networkError: raw.networkError as NetworkError | undefined,
    };
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type TokenGetter = () => Promise<string | null>;
export type TokenSetter = (token: string) => Promise<void>;

export interface RefreshedToken {
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
}

export interface AuthTokenProvider {
    getToken: TokenGetter;
    setToken?: TokenSetter;
    clearToken?: () => Promise<void>;
    /** Get the refresh token for token renewal */
    getRefreshToken?: () => Promise<string | null>;
    /** Called with the full refreshed token response (access + refresh + expires) */
    onTokenRefreshed?: (token: RefreshedToken) => void;
    /** Called when a cart permission error is received from the server */
    onCartError?: (errorMessage: string) => void;
    /** Server-side refresh endpoint URL (for Identity Auth) */
    serverRefreshEndpoint?: string;
}

export interface UnifiedClientConfig {
    uri?: string;
    authTokenProvider?: AuthTokenProvider;
    onAuthError?: (error: Error) => void;
    headers?: Record<string, string>;
}

/** Options for createCoreApolloClient */
export interface CoreApolloClientOptions {
    /** Override the API URL (defaults to appConfig.apiUrl) */
    apiUrl?: string;
    /** InMemoryCache configuration (typePolicies, etc.) */
    cacheConfig?: InMemoryCacheConfig;
    /** Enable SSR mode */
    ssrMode?: boolean;
    /** Custom fetch policy defaults */
    defaultOptions?: {
        watchQuery?: { fetchPolicy?: string };
        query?: { fetchPolicy?: string };
    };
}

// ============================================================================
// SINGLETON STATE
// ============================================================================

let unifiedClient: ApolloClient<NormalizedCacheObject> | null = null;
let authTokenProvider: AuthTokenProvider | null = null;
let customHeadersGetter: (() => Record<string, string>) | null = null;
let onAuthErrorCallback: ((error: Error) => void) | null = null;
let registeredCacheConfig: InMemoryCacheConfig | undefined;

// ============================================================================
// TOKEN REFRESH QUEUE
// ============================================================================

let isRefreshing = false;
let pendingRequests: Array<(token: string | null) => void> = [];

const processQueue = (error: Error | null, token: string | null = null): void => {
    pendingRequests.forEach(callback => {
        if (error) {
            callback(null);
        } else {
            callback(token);
        }
    });
    pendingRequests = [];
};

const delay = (milliseconds: number): Promise<boolean> =>
    new Promise(resolve => setTimeout(() => resolve(true), milliseconds));

// ============================================================================
// TOKEN REFRESH LOGIC
// ============================================================================

/**
 * Perform token refresh using raw fetch (bypasses Apollo to avoid circular error handling)
 */
async function doRefreshTokenRequest(refreshToken: string): Promise<RefreshedToken> {
    const apiUrl = appConfig.apiUrl;
    const customHeaders = customHeadersGetter ? customHeadersGetter() : getDefaultHeaders();

    if (!apiUrl) {
        throw new Error('[UnifiedClient] API URL is not defined for token refresh.');
    }

    const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...customHeaders,
        },
        body: JSON.stringify({
            query: `
                mutation RefreshToken($refreshToken: String!) {
                    refreshToken(refresh_token: $refreshToken) {
                        access_token
                        refresh_token
                        expires_in
                    }
                }
            `,
            variables: { refreshToken },
        }),
    });

    const result = await response.json();

    if (result.data?.refreshToken) {
        return result.data.refreshToken;
    }

    const errors: string[] = [];
    result.errors?.forEach((err: { message: string }) => {
        errors.push(err.message);
    });
    throw new Error(errors.join(', ') || 'Token refresh returned empty response');
}

/**
 * Refresh access token with queue pattern
 *
 * Only one refresh request runs at a time. All concurrent auth failures
 * are queued and resolved when the refresh completes.
 */
async function refreshAccessToken(): Promise<void> {
    if (isRefreshing) return;
    isRefreshing = true;

    try {
        const refreshToken = await authTokenProvider?.getRefreshToken?.();

        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        const response = await doRefreshTokenRequest(refreshToken);

        if (response?.access_token) {
            // Update token via the provider
            await authTokenProvider?.setToken?.(response.access_token);

            // Notify about full token response (including new refresh_token)
            authTokenProvider?.onTokenRefreshed?.(response);

            // Wait for token propagation in storage/state
            await delay(200);

            processQueue(null, response.access_token);
        } else {
            logSentry({
                name: 'Refresh token failed',
                transactionName: 'refreshAccessToken',
                error: new Error('Refresh token returned invalid response'),
                extra: { response },
            });
            await authTokenProvider?.clearToken?.();
            onAuthErrorCallback?.(new Error('Refresh token failed'));
            processQueue(new Error('Refresh token failed'), null);
        }
    } catch (error) {
        logSentry({
            name: 'Refresh token failed with exception',
            transactionName: 'refreshAccessToken',
            error: error as Error,
            extra: {},
        });
        await authTokenProvider?.clearToken?.();
        onAuthErrorCallback?.(error as Error);
        processQueue(error as Error, null);
    } finally {
        isRefreshing = false;
    }
}

/**
 * Handle token refresh promise for queued requests
 */
const handleRefreshToken = (
    resolve: (value?: void | PromiseLike<void>) => void,
    reject: (reason?: Error) => void,
    token: string | null
): void => {
    if (token) {
        resolve();
    } else {
        reject(new Error('Refresh token failed'));
    }
};

/**
 * Queue a request and trigger refresh if not already running
 */
const handleRefreshPromise = async (
    resolve: (value?: void | PromiseLike<void>) => void,
    reject: (reason?: Error) => void
): Promise<void> => {
    pendingRequests.push(token => handleRefreshToken(resolve, reject, token));

    // Server-side refresh: no client-side refresh token needed
    if (authTokenProvider?.serverRefreshEndpoint) {
        if (!isRefreshing) {
            refreshAccessToken().catch(async error => {
                await authTokenProvider?.clearToken?.();
                reject(error);
            });
        }
        return;
    }

    // Legacy: client-side refresh using refresh token from JWT
    const refreshToken = await authTokenProvider?.getRefreshToken?.();

    if (!isRefreshing && refreshToken) {
        refreshAccessToken().catch(async error => {
            await authTokenProvider?.clearToken?.();
            reject(error);
        });
    } else if (!refreshToken) {
        reject(new Error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.'));
        await authTokenProvider?.clearToken?.();
    }
};

// ============================================================================
// AUTH ERROR DETECTION
// ============================================================================

const AUTH_ERROR_MESSAGES = [
    'Unauthorized',
    "The current customer isn't authorized.",
    'The request is allowed for logged in customer',
    'Customer not logged in.',
] as const;

function isAuthError(message: string): boolean {
    return AUTH_ERROR_MESSAGES.includes(message as (typeof AUTH_ERROR_MESSAGES)[number]);
}

// ============================================================================
// CART PERMISSION ERROR DETECTION
// ============================================================================

const CART_ERROR_MESSAGES = [
    'The current user cannot perform operations on cart',
    'Current user does not have an active cart.',
] as const;

function isCartPermissionError(message: string): boolean {
    return CART_ERROR_MESSAGES.some(errorMsg => message.includes(errorMsg));
}

// ============================================================================
// SHARED LINK CREATORS
// ============================================================================

/**
 * Retry a failed operation after refreshing the auth token
 * Extracted to reduce nesting depth in error link handler
 */
async function retryWithFreshToken(operation: Operation, forward: NextLink): Promise<Observable<FetchResult>> {
    await new Promise<void>(handleRefreshPromise);
    const freshToken = await authTokenProvider?.getToken?.();

    operation.setContext(({ headers }: DefaultContext) => ({
        headers: {
            ...headers,
            authorization: freshToken ? `Bearer ${freshToken}` : '',
        },
    }));

    return forward(operation);
}

/**
 * Handle cart permission error: log to Sentry and notify callback
 */
function handleCartError(cartError: GraphQLFormattedError, operationName: string): void {
    console.warn('[UnifiedClient] Cart permission error detected:', cartError.message);
    logSentry({
        name: 'Cart Permission Error',
        transactionName: operationName,
        extra: { operationName, message: cartError.message },
        error: { ...cartError, name: operationName } as unknown as Error,
    });

    try {
        authTokenProvider?.onCartError?.(cartError.message);
    } catch (callbackError) {
        console.error('[UnifiedClient] Error in onCartError callback:', callbackError);
    }
}

/**
 * Log GraphQL errors to Sentry
 */
function logGraphQLErrors(errors: ReadonlyArray<GraphQLFormattedError>, operationName: string): void {
    for (const err of errors) {
        const safeError = new Error(err.message ?? 'Unknown GraphQL error');
        safeError.name = operationName;
        logSentry({
            name: 'GraphQL Error',
            transactionName: operationName,
            extra: { operationName, path: err.path },
            error: safeError,
        });
    }
}

/**
 * Log network error to Sentry
 */
function logNetworkError(networkError: NonNullable<NetworkError>, operationName: string): void {
    if (networkError.message === 'Failed to fetch') {
        console.error('[UnifiedClient] No internet connection');
    } else {
        console.error(`[UnifiedClient] Network error [${operationName}]: ${networkError.message}`);
    }
    const safeError = new Error(networkError.message ?? 'Network error');
    safeError.name = operationName;
    logSentry({
        name: 'GraphQL Network Error',
        transactionName: operationName,
        extra: { operationName },
        error: safeError,
    });
}

/**
 * Create error link with full token refresh and retry logic
 *
 * Provides:
 * - Cart permission error detection → calls onCartError callback
 * - Auth error detection → refreshes token and retries the operation
 * - Sentry logging for all GraphQL and network errors
 *
 * The error link reads authTokenProvider at request time,
 * so it works even when created before setupCoreIntegration runs.
 */
export function createErrorLink() {
    return onError(errorResponse => {
        const { operation, forward } = errorResponse;
        const { graphQLErrors, networkError } = extractErrorDetails(errorResponse);

        if (graphQLErrors) {
            // 1. Cart permission error: clear stores and let error propagate
            const cartError = graphQLErrors.find(err => isCartPermissionError(err.message));
            if (cartError) {
                handleCartError(cartError, operation.operationName);
                return;
            }

            // 2. Auth error: refresh token and retry the operation
            const authError = graphQLErrors.find(err => isAuthError(err.message));
            if (authError) {
                return observableFromPromise(retryWithFreshToken(operation, forward)).flatMap(obs => obs);
            }

            // 3. Other errors: log all to Sentry
            logGraphQLErrors(graphQLErrors, operation.operationName);
            return forward(operation);
        }

        if (networkError) {
            logNetworkError(networkError, operation.operationName);
            return forward(operation);
        }

        return forward(operation);
    });
}

/**
 * Default headers fallback when customHeadersGetter is not yet registered.
 *
 * Provides essential headers from appConfig for requests that happen before
 * setupCoreIntegration runs (e.g., during initial render or early useEffect calls).
 *
 * In QueryProvider, initializeCoreIntegration runs in useEffect (after first render).
 * Child useEffect hooks fire BEFORE parent useEffect, so a page component can
 * trigger a service request before the integration is wired up — this fallback
 * ensures those early requests still carry the required store-code and API key.
 */
function getDefaultHeaders(): Record<string, string> {
    const isClient = typeof window !== 'undefined';
    const storeCode = appConfig.storeCode || 'default';

    return {
        'magento-store-code': storeCode,
        store: storeCode,
        'X-API-KEY': isClient ? (process.env.NEXT_PUBLIC_API_KEY ?? '') : (process.env.API_KEY_SERVER ?? ''),
    };
}

/**
 * Create auth link that attaches token and custom headers.
 *
 * Reads customHeadersGetter at REQUEST time (not link-creation time) so that
 * headers are always up-to-date, even for clients cached before setupCoreIntegration.
 * Falls back to getDefaultHeaders() when the getter has not been registered yet.
 */
export function createAuthLink() {
    return setContext(async (_, { headers }) => {
        const token = await getAuthToken();
        const customHeaders = customHeadersGetter ? customHeadersGetter() : getDefaultHeaders();

        return {
            headers: {
                ...headers,
                ...customHeaders,
                ...(token && { authorization: `Bearer ${token}` }),
            },
        };
    });
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Set the unified Apollo Client
 *
 * Called ONCE by the main app during initialization.
 * The provided client will be used by all modules and services.
 */
export function setUnifiedClient(client: ApolloClient<NormalizedCacheObject>, tokenProvider?: AuthTokenProvider): void {
    unifiedClient = client;
    if (tokenProvider) {
        authTokenProvider = tokenProvider;
    }
    console.log('[UnifiedClient] Client set successfully');
}

export function setAuthTokenProvider(provider: AuthTokenProvider): void {
    authTokenProvider = provider;
}

export function setCustomHeadersGetter(getter: () => Record<string, string>): void {
    customHeadersGetter = getter;
}

export function setOnAuthError(callback: (error: Error) => void): void {
    onAuthErrorCallback = callback;
}

/**
 * Register InMemoryCache configuration (typePolicies, etc.)
 *
 * Called during app setup so that the fallback client created by
 * getUnifiedClient() uses the same cache config as the app's client.
 */
export function setCoreClientCacheConfig(config: InMemoryCacheConfig): void {
    registeredCacheConfig = config;
}

/**
 * Get the unified Apollo Client
 *
 * Returns the singleton client. If not yet initialized,
 * creates a default client with full token refresh support.
 */
export function getUnifiedClient(): ApolloClient<NormalizedCacheObject> {
    if (!unifiedClient) {
        console.warn('[UnifiedClient] Client not initialized, creating default client with token refresh');
        unifiedClient = createDefaultClient();
    }
    return unifiedClient;
}

export function isUnifiedClientInitialized(): boolean {
    return unifiedClient !== null;
}

export async function getAuthToken(): Promise<string | null> {
    if (!authTokenProvider) {
        console.warn('[UnifiedClient] No auth token provider registered');
        return null;
    }
    return authTokenProvider.getToken();
}

export async function setAuthToken(token: string): Promise<void> {
    if (!authTokenProvider?.setToken) {
        console.warn('[UnifiedClient] No auth token setter registered');
        return;
    }
    await authTokenProvider.setToken(token);
}

export async function clearAuthToken(): Promise<void> {
    if (!authTokenProvider?.clearToken) {
        console.warn('[UnifiedClient] No auth token clear function registered');
        return;
    }
    await authTokenProvider.clearToken();
}

export function resetUnifiedClient(): void {
    unifiedClient = null;
    authTokenProvider = null;
    customHeadersGetter = null;
    onAuthErrorCallback = null;
    registeredCacheConfig = undefined;
    isRefreshing = false;
    pendingRequests = [];
}

// ============================================================================
// CLIENT CREATION
// ============================================================================

/**
 * Create a fully-featured Apollo Client with token refresh
 *
 * This client has the same capabilities as the website's main Apollo client:
 * - Auth header injection via authLink
 * - Token refresh with queue pattern via errorLink
 * - Custom headers (store code, API key, etc.) via bridge
 * - Sentry error logging
 *
 * Use this when you want the core module to OWN the Apollo client creation.
 */
export function createCoreApolloClient(options?: CoreApolloClientOptions): ApolloClient<NormalizedCacheObject> {
    console.log('[UnifiedClient] Creating core Apollo Client with options:', options);
    const uri = options?.apiUrl ?? appConfig.apiUrl;

    const errorLink = createErrorLink();
    const authLink = createAuthLink();
    const httpLink = new HttpLink({ uri, credentials: 'same-origin' });

    return new ApolloClient({
        ssrMode: options?.ssrMode ?? globalThis.window === undefined,
        link: ApolloLink.from([authLink, errorLink, httpLink]),
        cache: new InMemoryCache(options?.cacheConfig),
    });
}

/**
 * Create default Apollo Client (internal fallback)
 *
 * Uses registeredCacheConfig if available, so the fallback client
 * has the same cache behaviour (typePolicies, etc.) as the app's client.
 */
function createDefaultClient(): ApolloClient<NormalizedCacheObject> {
    return createCoreApolloClient({
        apiUrl: appConfig.apiUrl,
        cacheConfig: registeredCacheConfig,
    });
}

// ============================================================================
// LEGACY COMPATIBILITY
// ============================================================================

/** @deprecated Use setUnifiedClient() or createCoreApolloClient() instead */
export async function initClient(
    config?: UnifiedClientConfig
): Promise<{ client: ApolloClient<NormalizedCacheObject> }> {
    if (config?.authTokenProvider) {
        authTokenProvider = config.authTokenProvider;
    }
    if (config?.onAuthError) {
        onAuthErrorCallback = config.onAuthError;
    }

    const client = getUnifiedClient();
    return { client };
}
